import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthUserDTO, ModuleKey } from '@six-pockets/shared';
import { ApiRequestError, authApi, setAuth } from '@/api';

const STORAGE_KEY = 'six-pockets.authToken';
/**
 * Before authTokens, the Basic credentials (with the password) were stored under this key: first in sessionStorage,
 * then in localStorage. Removed from both on load.
 */
const LEGACY_KEY = 'six-pockets.auth';

interface AuthContextValue {
  /** Null while signed out. */
  user: AuthUserDTO | null;
  /** True while a stored authToken is being checked on page load. */
  restoring: boolean;
  /** Throws with a user-facing message if the password is wrong. */
  signIn: (username: string, password: string) => Promise<void>;
  signOut: () => void;
  can: (module: ModuleKey) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// The authToken is kept in localStorage, shared by every tab, so a new tab or a refresh stays signed in until Sign out
// or until the token's 3 days are up. Storage can be unavailable; then a refresh signs out.
const storage = {
  get: () => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  },
  set: (value: string | null) => {
    try {
      if (value) localStorage.setItem(STORAGE_KEY, value);
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  },
};

try {
  localStorage.removeItem(LEGACY_KEY);
  sessionStorage.removeItem(LEGACY_KEY);
} catch {
  // ignore
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUserDTO | null>(null);
  const [restoring, setRestoring] = useState(() => storage.get() !== null);

  // Back to the login page here and in every tab (the storage event), without telling the server: used when the
  // server already refused the authToken (expired, or signed out elsewhere).
  const dropToken = useCallback(() => {
    setAuth(null);
    storage.set(null);
    setUser(null);
  }, []);

  // Sign out: also ends the authToken on the server, so it can't be used again. Other counters stay signed in.
  const signOut = useCallback(() => {
    const token = storage.get();
    dropToken();
    if (token) authApi.logout(token).catch(() => undefined);
  }, [dropToken]);

  const accept = useCallback(
    (token: string, me: AuthUserDTO) => {
      setAuth(token, dropToken);
      storage.set(token);
      setUser(me);
    },
    [dropToken],
  );

  useEffect(() => {
    const token = storage.get();
    if (!token) return;
    authApi
      .me(token)
      .then((me) => accept(token, me))
      .catch((err) => {
        // Only a refused token is thrown away; if the server can't be reached it may still be good later.
        if (err instanceof ApiRequestError && err.status === 401) storage.set(null);
      })
      .finally(() => setRestoring(false));
  }, [accept]);

  // Keep the tabs in step: signing out (or in as someone else) in one tab does the same here.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY && e.key !== null) return;
      const token = storage.get();
      if (!token) {
        setAuth(null);
        setUser(null);
        return;
      }
      authApi.me(token).then(
        (me) => accept(token, me),
        () => undefined,
      );
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [accept]);

  const signIn = useCallback(
    async (username: string, password: string) => {
      try {
        const res = await authApi.login({ username: username.trim().toLowerCase(), password });
        accept(res.authToken, res.user);
      } catch (err) {
        if (err instanceof ApiRequestError && err.status === 401) throw new Error('Incorrect username or password');
        if (err instanceof ApiRequestError && err.status === 503) throw new Error(err.message);
        throw new Error('Could not reach the server. Try again.');
      }
    },
    [accept],
  );

  const value = useMemo<AuthContextValue>(
    () => ({ user, restoring, signIn, signOut, can: (m) => !!user?.access[m] }),
    [user, restoring, signIn, signOut],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
